// O MENU DO PORTAL `/banco` — por modo do credor e por permissao do operador.
//
// Dois portoes, nesta ordem (#314):
//
//  1. MODO DO CREDOR (`bancos.modo_portal`, configurado pela averbadora, #313):
//     `credito` ve o portal de banco; `operadora` ve os beneficios; `ambos`, os
//     dois. Item fora do modo SOME — um banco nao deve ver "Beneficios"
//     desabilitado, porque nao ha nada que ele possa fazer para alcanca-lo.
//  2. PERMISSAO DO OPERADOR (claim `banco_permissoes`): o que a pessoa alcanca
//     dentro do modo. Ausente = login-master, que ve tudo.
//
// Acrescentar item = declarar o `escopo` (`credito` | `beneficios` | `comum`).
// Filho de grupo herda o escopo do recurso (`BANCO_RESOURCE_GROUPS`). O menu e'
// conveniencia: quem barra e' a API (`requireModoOperadora` + `requireBancoPermissao`).

import type { ReactNode } from "react";
import {
  bancoEscopoNoModo,
  bancoPermissaoCobre,
  bancoPodeAverbarPelaFicha,
  bancoPodeConsultarMargem,
  bancoRecursoNoModo,
  type BancoEscopoRecurso,
  type BancoModoPortal,
} from "../../lib/banco-perms";

export interface ItemNavBanco {
  key: string;
  label: string;
  href?: string;
  icon?: ReactNode;
  children?: ItemNavBanco[];
  /** Contador ao lado do rotulo (zero nao aparece). */
  badge?: number;
}

interface ItemDeclarado extends ItemNavBanco {
  escopo: BancoEscopoRecurso;
  /** Chave de permissao, quando difere da `key` do item. Uma lista = qualquer uma delas. */
  recurso?: string | readonly string[];
  children?: ItemNavBanco[];
}

/** O lado dos beneficios. Em `ambos`, vem acima do credito. */
const NAV_BENEFICIOS: ItemDeclarado[] = [
  { key: "visao-do-mes", label: "Visão do mês", href: "/banco/visao-do-mes", icon: "◉", escopo: "beneficios", recurso: "visao-geral" },
  { key: "beneficios", label: "Benefícios", href: "/banco/beneficios", icon: "✚", escopo: "beneficios" },
  // #318: ver a fila basta `beneficios`; responder exige `solicitacoes` (a tela decide os botoes).
  // "Solicitacoes": cancelamento e alteracao pedidos pelo servidor (a rota guarda o nome antigo).
  {
    key: "pedidos-cancelamento",
    label: "Solicitações",
    href: "/banco/pedidos-cancelamento",
    icon: "✉",
    escopo: "beneficios",
    recurso: ["solicitacoes", "beneficios"],
  },
];

const cobreAlgum = (permissoes: readonly string[] | undefined, recurso: string | readonly string[]) =>
  (typeof recurso === "string" ? [recurso] : recurso).some((r) => bancoPermissaoCobre(permissoes, r));

/** O menu de credito de sempre, com os itens comuns na posicao de hoje: em
 *  `credito` ele sai identico ao que era antes do modo existir. */
const NAV_CREDITO_E_COMUM: ItemDeclarado[] = [
  { key: "visao-geral", label: "Visão Geral", href: "/banco/visao-geral", icon: "◉", escopo: "credito" },
  {
    key: "propostas",
    label: "Propostas e Portabilidade",
    icon: "◈",
    escopo: "credito",
    children: [
      { key: "emprestimo", label: "Empréstimo", href: "/banco/propostas?produto=emprestimo" },
      { key: "cartao", label: "Cartão", href: "/banco/propostas?produto=cartao" },
      { key: "portabilidade", label: "Portabilidade", href: "/banco/portabilidade" },
      { key: "cartao_beneficio", label: "Cartão Benefício", href: "/banco/propostas?produto=cartao_beneficio" },
      { key: "refinanciamento", label: "Refinanciamento", href: "/banco/refinanciamentos" },
    ],
  },
  { key: "ofertas", label: "Ofertas", href: "/banco/ofertas", icon: "◇", escopo: "credito" },
  {
    key: "cadastros",
    label: "Cadastros",
    icon: "▢",
    escopo: "comum",
    children: [
      { key: "tabela-emprestimos", label: "Tabela de Empréstimos", href: "/banco/cadastros/tabela-emprestimos" },
      { key: "sub-organizacoes", label: "Sub organizações", href: "/banco/cadastros/sub-organizacoes" },
      { key: "usuarios", label: "Operadores", href: "/banco/cadastros/usuarios" },
    ],
  },
  { key: "margem-contratacao", label: "Margem / Contratação", href: "/banco/margem-contratacao", icon: "▤", escopo: "credito" },
  { key: "carteira", label: "Meus Contratos", href: "/banco/carteira", icon: "▥", escopo: "credito" },
  { key: "bate-carteira", label: "Bate de Carteira", href: "/banco/bate-carteira", icon: "▦", escopo: "credito" },
  { key: "retornos", label: "Troca de arquivos", href: "/banco/troca-arquivos", icon: "⇄", escopo: "comum" },
  { key: "convenios", label: "Convênios", href: "/banco/convenios", icon: "◈", escopo: "credito" },
  {
    key: "relatorios",
    label: "Relatórios",
    icon: "▦",
    escopo: "credito",
    children: [
      { key: "consignacoes", label: "Consignações", href: "/banco/relatorios/consignacoes" },
      { key: "gerador", label: "Gerador", href: "/banco/relatorios/gerador" },
      { key: "faturamento", label: "Faturamento (comissão)", href: "/banco/relatorios/faturamento" },
    ],
  },
  // Comum: quem so' opera beneficios tambem integra pela API.
  { key: "api", label: "API e documentação", href: "/banco/api", icon: "⌗", escopo: "comum" },
  { key: "conta", label: "Minha conta", href: "/banco/conta", icon: "◐", escopo: "comum" },
];

/** O item sem os campos de declaracao — e' o que o `AppShellAdmin` recebe. */
function semDeclaracao({ escopo: _e, recurso: _r, ...item }: ItemDeclarado): ItemNavBanco {
  return item;
}

/**
 * O menu que este operador ve, neste credor.
 *
 * `modo` ausente (o `me` ainda nao chegou) e' `credito`: o menu de hoje, sem
 * piscar beneficios para um banco.
 */
export function navDoBanco(
  modo: BancoModoPortal | null | undefined,
  permissoes: readonly string[] | undefined,
  /** Contadores por `key` de item (ex.: pedidos de cancelamento abertos). */
  badges: Readonly<Record<string, number | undefined>> = {},
): ItemNavBanco[] {
  const declarados = [...NAV_BENEFICIOS, ...NAV_CREDITO_E_COMUM].filter((i) => bancoEscopoNoModo(i.escopo, modo));
  return declarados
    .map((item): ItemNavBanco | null => {
      const visivel = itemVisivel(item, modo, permissoes);
      return visivel && badges[item.key] ? { ...visivel, badge: badges[item.key] } : visivel;
    })
    .filter((x): x is ItemNavBanco => x !== null);
}

function itemVisivel(
  item: ItemDeclarado,
  modo: BancoModoPortal | null | undefined,
  permissoes: readonly string[] | undefined,
): ItemNavBanco | null {
  // Margem tem duas chaves: `margem-contratacao` (consulta + averbacao) e
  // `margem-consulta` (so' leitura). Qualquer uma abre a tela — mesmo portao
  // da API — e o rotulo nao promete "Contratacao" a quem so' consulta.
  if (item.key === "margem-contratacao") {
    if (!bancoPodeConsultarMargem(permissoes)) return null;
    const limpo = semDeclaracao(item);
    return bancoPodeAverbarPelaFicha(permissoes) ? limpo : { ...limpo, label: "Margem (consulta)" };
  }
  const recurso = item.recurso ?? item.key;
  if (!item.children) {
    return cobreAlgum(permissoes, recurso) ? semDeclaracao(item) : null;
  }
  // Filho herda o escopo do recurso dele: em `operadora`, Cadastros perde a
  // Tabela de Emprestimos (credito) e fica com Sub organizacoes e Operadores.
  const doModo = item.children.filter((c) => bancoRecursoNoModo(c.key, modo));
  const paiCobre = cobreAlgum(permissoes, recurso);
  const filhos = doModo.filter((c) => paiCobre || bancoPermissaoCobre(permissoes, c.key));
  if (filhos.length === 0) return null;
  return { ...semDeclaracao(item), children: filhos };
}

/** A tela de entrada do portal: a Visao do mes para quem opera beneficios. */
export function inicioDoBanco(modo: BancoModoPortal | null | undefined): string {
  return bancoEscopoNoModo("beneficios", modo) ? "/banco/visao-do-mes" : "/banco/visao-geral";
}
